import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { downloadFile } from '../../lib/api';
import { arcaInvoiceStatus, dateLabel, fiscalInvoiceNo, money, customerShort } from '../../lib/format';
import type { ArcaInvoice } from '../../lib/types';
import { describeInvoicePdfError, invoicePdfFilename, invoicePdfPath } from './invoicePdf';

// ARCA 税票 · 开票列表（I02 只读基线，**不受** INTEGRAL_ENABLE_FINANCE_READ 门控）。
//
// 为什么本页不门控：这里只陈述「发票已由 Factura 授权登记」这一既成事实，并让财务
// 能随时打印三联发票 PDF（GET /api/arca-invoices 与 /api/arca-invoices/:id/pdf 后端
// 本就不受门控）。门控关掉的是**应收口径**（已核销 / 未收余额 / 收款 / 原始报文 / 附件）
// —— 那些在「ARCA 财务专页」，受 INTEGRAL_ENABLE_FINANCE_READ 控制。
// 把两者拆开的直接收益：生产环境门控关闭时，发票依然可查可打印，不会因为一个环境
// 开关而丢掉开票凭证的查询能力。
//
// 三处刻意留空，避免把未定口径做成既成事实：
//   · 部分开票（partially_invoiced）：合同 USD × 开票汇率 ↔ 发票 ARS 的核对规则待财务确认，
//     core-api 亦未实现（app.js 明文预留），本页不做推算；
//   · 已授权票据的更正/取消：Factura 侧走 ARCA 更正流程，本地不得抹除税务授权；
//   · 电子支票到账：不把「已登记收款」当成「已到账」（相关金额只在财务专页出现）。
function InvoiceDetail({ row }: { row: ArcaInvoice }) {
  const { data, loading, error } = useDetail<ArcaInvoice>(`/api/arca-invoices/${row.id}`);
  const invoice = data ?? row;
  const st = arcaInvoiceStatus(invoice.status);

  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfErr(null);
    setPdfBusy(true);
    try {
      await downloadFile(invoicePdfPath(invoice.id), invoicePdfFilename(invoice));
    } catch (e) {
      setPdfErr(describeInvoicePdfError(e));
    } finally { setPdfBusy(false); }
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        {/* 对外标识统一用税务局票号；内部编号只在 title 里留作排查线索。
            现场口径（2026-10-29）：内部发票号不再采用，客户与税务单据只认税务局票号。 */}
        <span className="id" title={invoice.internal_number ? `内部编号 ${invoice.internal_number}` : ''}>
          {fiscalInvoiceNo(invoice)}
        </span>
        <h3>{invoice.customer_name || invoice.customer_id}</h3>
        <Pill tone={st.tone} label={st.label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
        <button className="btn" disabled={!invoice.cae || pdfBusy} onClick={() => void downloadPdf()}
                title={invoice.cae ? '下载三联发票 PDF（原票扫描/渲染件）' : '无 CAE，无法定位税务 PDF'}>
          {pdfBusy ? '下载中…' : '三联发票 PDF'}
        </button>
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {pdfErr ? <div className="notice bad">{pdfErr}</div> : null}

      <div className="kv-grid">
        <KeyValue k="票种">{invoice.voucher_type}</KeyValue>
        <KeyValue k="销售点 / 凭证号">{`${invoice.point_of_sale || '—'} · ${invoice.voucher_number ?? '—'}`}</KeyValue>
        <KeyValue k="开票日期">{dateLabel(invoice.invoice_date)}</KeyValue>
        <KeyValue k="到期日">{dateLabel(invoice.due_date)}</KeyValue>
        <KeyValue k="合同">{invoice.contract_number || invoice.contract_id || '—'}</KeyValue>
        <KeyValue k="客户税号">{invoice.customer_tax_id || '—'}</KeyValue>
        <KeyValue k="币种">{invoice.currency}</KeyValue>
        <KeyValue k="净额 / 税额">{`${money(invoice.net_total, invoice.currency)} / ${money(invoice.tax_total, invoice.currency)}`}</KeyValue>
        <KeyValue k="总额（原币）">{money(invoice.grand_total, invoice.currency)}</KeyValue>
        <KeyValue k="CAE">{invoice.cae || '—'}</KeyValue>
        <KeyValue k="CAE 到期">{dateLabel(invoice.cae_due_date)}</KeyValue>
        <KeyValue k="来源">{`${invoice.source_system || '—'} · ${invoice.source_id || '—'}`}</KeyValue>
        <KeyValue k="二维码">{invoice.qr_url || '—'}</KeyValue>
      </div>

      {/* 应收口径不在这里：本页不受门控，而「已核销 / 未收余额 / 原始报文 / 附件」都受控。
          给一条明确的路，而不是让人以为数字被藏起来是 bug。 */}
      <div className="muted" style={{ fontSize: 12.5, margin: '6px 0 12px' }}>
        {/* 跳转用税务局票号做检索词：财务专页的搜索列已含 i.voucher_number，
            且内部编号按现场口径不再对外使用。 */}
        应收与核销口径不在本页：<Link to={`/arca-finance?q=${encodeURIComponent(fiscalInvoiceNo(invoice))}`}>财务专页（应收/核销/原始报文）</Link>
        <span> · 该页受环境开关 INTEGRAL_ENABLE_FINANCE_READ 控制。</span>
      </div>
    </div>
  );
}

/**
 * 页面入口（开票列表）。
 *
 * 本页**刻意不调用 useFinanceGate()**：门控只影响应收/收款（见 ArcaFinancePage）。
 * 拿门控来挡这一页会让生产环境连发票号与 PDF 都查不到，与拆页的目标相反。
 */
export function ArcaInvoicesPage() {
  // 合同详情「关联单据 → 发票」会带 ?q=<合同号> 跳进来（见 ContractDetail）。
  // 从合同点进来时列表应直接收敛到该合同的票，而不是让人再从空列表里重搜。
  const [sp] = useSearchParams();
  // 行内 PDF 下载的失败提示：放在页面顶部（列表上方），因为按钮所在行可能已被滚动出视野。
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const downloadRow = async (r: ArcaInvoice) => {
    setPdfErr(null);
    try {
      await downloadFile(invoicePdfPath(r.id), invoicePdfFilename(r));
    } catch (e) {
      // 不用 alert：alert 会打断整页、且无法复制；notice 行与其它页面一致。
      setPdfErr(`${fiscalInvoiceNo(r)}：${describeInvoicePdfError(e)}`);
    }
  };

  const columns: Column<ArcaInvoice>[] = [
    // 税务局票号是唯一对外标识（现场口径：内部发票号不再采用）。
    // 内部编号降级到 title，仅供排查时对照 —— 它既不在税务单据上，客户也不认。
    { header: '税务局票号', render: (r) => (
      <span className="num strong" title={r.internal_number ? `内部编号 ${r.internal_number}` : ''}>
        {fiscalInvoiceNo(r)}
      </span>
    ) },
    { header: '票种', render: (r) => r.voucher_type },
    { header: '开票日期', render: (r) => dateLabel(r.invoice_date) },
    { header: '客户', render: (r) => <span title={r.customer_name || ''}>{customerShort(r.customer_name || r.customer_id)}</span> },
    { header: '合同', render: (r) => r.contract_number || '—' },
    // 金额只给原币：ARS 折算、已核销、未收余额属应收口径，放在受门控的财务专页。
    { header: '总额（原币）', align: 'right', render: (r) => money(r.grand_total, r.currency) },
    // CAE 是 14 位长串，全量显示会把表格撑宽；截断保留可辨识的前 10 位，title 给全量便于复制核对。
    { header: 'CAE', render: (r) => (r.cae
      ? <span className="num" title={r.cae}>{`${String(r.cae).slice(0, 10)}…`}</span>
      : <span className="muted">—</span>) },
    { header: '状态', render: (r) => { const s = arcaInvoiceStatus(r.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <>
      {pdfErr ? <div className="notice bad" style={{ marginBottom: 'var(--gap)' }}>{pdfErr}</div> : null}
      <ListPage<ArcaInvoice>
        title="ARCA 税票 · 开票列表"
        desc="发票登记与三联 PDF 下载；应收/收款口径在财务专页，受环境开关控制。"
        endpoint="/api/arca-invoices"
        rowId={(r) => r.id}
        columns={columns}
        rowActions={(r) => (
          <button
            className="btn link"
            // 无 CAE 的票在税务账本里定位不到，后端必然 404；按钮直接禁用比让人点了再报错好。
            disabled={!r.cae}
            title={r.cae ? `下载 ${fiscalInvoiceNo(r)} 的三联发票 PDF` : '无 CAE，无法定位税务 PDF'}
            onClick={(e) => {
              // 行点击会展开详情；下载按钮必须阻断冒泡，否则点一次既下载又展开。
              e.stopPropagation();
              void downloadRow(r);
            }}
          >
            三联发票 PDF
          </button>
        )}
        renderDetail={(row) => <InvoiceDetail row={row} />}
        searchPlaceholder="搜索税务局票号 / CAE / 客户 / 合同号 / 客户合同号…"
        initialQuery={sp.get('q') || ''}
      />
    </>
  );
}
